import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Form, Input, Select, TextArea, Toggle } from '@/components';

import { BiogasProjectTechnology } from '@/features/projects/types';

import { getDateFromQuarterAndYear, getQuarterFromDate, getYearFromDate, nullableStringSetter } from '@/helpers';
import { useDevelopmentStatus } from '@/hooks';
import { useUpgradingTechnology } from '@/features/projects/hooks/use-upgrading-technology';
import { NUMERIC } from '@/features/projects/helpers/numeric-inputs';

export type BiogasSubmitFields = {
  capacity: number | null;
  development_status: string | null;
  ready_to_build: string | null;
  commercial_operation_date: string | null;
  feedstock_supply_radius: number | null;
  feedstock_cost: number | null;
  upgrading_included: boolean | null;
  upgrading_technology: string | null;
  information: string | null;
};

export type BiogasFormFields = {
  capacity: number | null;
  development_status: string | null;
  ready_to_build: string | null;
  ready_to_build_quarter: string | null;
  ready_to_build_year: number | null;
  commercial_operation_date: string | null;
  commercial_operation_date_quarter: string | null;
  commercial_operation_date_year: number | null;
  feedstock_supply_radius: number | null;
  feedstock_cost: number | null;
  upgrading_included: boolean | null;
  upgrading_technology: string | null;
  information: string | null;
};

export type BiogasFormProps = {
  readonly id: string;
  readonly item: BiogasProjectTechnology;
  readonly onSubmit: (data: BiogasSubmitFields) => void;
};

export function BiogasFormNew({ id, item, onSubmit }: BiogasFormProps) {
  const { t } = useTranslation(['projects']);
  const { options: developmentStatusOptions } = useDevelopmentStatus();
  const { options: upgradingTechnologyOptions } = useUpgradingTechnology();

  const quarterOptions = [
    { label: 'Q1', value: '1' },
    { label: 'Q2', value: '2' },
    { label: 'Q3', value: '3' },
    { label: 'Q4', value: '4' },
  ];

  const options = useMemo(
    () => ({
      defaultValues: {
        capacity: item.capacity ?? null,
        development_status: item.development_status ?? null,
        ready_to_build: item.ready_to_build ?? null,
        ready_to_build_quarter: getQuarterFromDate(item.ready_to_build),
        ready_to_build_year: getYearFromDate(item.ready_to_build),
        commercial_operation_date: item.commercial_operation_date ?? null,
        commercial_operation_date_quarter: getQuarterFromDate(item.commercial_operation_date),
        commercial_operation_date_year: getYearFromDate(item.commercial_operation_date),
        information: item.information ?? null,
        feedstock_supply_radius: item.feedstock_supply_radius ?? null,
        feedstock_cost: item.feedstock_cost ?? null,
        upgrading_included: item.upgrading_included ?? null,
        upgrading_technology: item.upgrading_technology ?? null,
      },
    }),
    [item]
  );

  const onSubmitBiogasForm = useCallback(
    (data: BiogasFormFields) => {
      data.ready_to_build = getDateFromQuarterAndYear(data.ready_to_build_quarter, data.ready_to_build_year);
      data.commercial_operation_date = getDateFromQuarterAndYear(
        data.commercial_operation_date_quarter,
        data.commercial_operation_date_year
      );
      const payload = {
        capacity: data.capacity,
        development_status: data.development_status,
        ready_to_build: data.ready_to_build,
        commercial_operation_date: data.commercial_operation_date,
        feedstock_supply_radius: data.feedstock_supply_radius,
        feedstock_cost: data.feedstock_cost,
        upgrading_included: data.upgrading_included,
        upgrading_technology: data.upgrading_technology,
        information: data.information,
      };

      onSubmit(payload);
    },
    [onSubmit]
  );

  return (
    <Form<BiogasFormFields> id={id} onSubmit={(data) => onSubmitBiogasForm(data)} options={options}>
      {({ formState, register }) => (
        <div className="space-y-6">
          <Field
            id="capacity"
            label={t('Installed Capacity')}
            error={formState.errors['capacity']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="capacity"
              type="number"
              suffix="GWh"
              placeholder={t('Example: 300')}
              color={formState.errors['capacity'] ? 'error' : 'primary'}
              autoComplete="off"
              registration={register('capacity', {
                ...NUMERIC,
              })}
            />
          </Field>
          <Field
            id="comments"
            label={t('Development status')}
            error={formState.errors['development_status']}
            delay={0.45}
          >
            <Select
              color="primary"
              registration={register('development_status')}
              options={developmentStatusOptions}
              className="mt-2"
            />
          </Field>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Field
              id="ready_to_build"
              label={t('Ready to Build (RtB) Date')}
              error={formState.errors['ready_to_build']}
              delay={0.25}
              classes={{ field: 'space-y-1' }}
            >
              <div className="flex gap-1">
                <Select
                  id="ready_to_build_quarter"
                  options={quarterOptions}
                  placeholder={t('Select a quarter')}
                  color={formState.errors['ready_to_build_quarter'] ? 'error' : 'primary'}
                  registration={register('ready_to_build_quarter')}
                />
                <Input
                  id="ready_to_build_year"
                  type="number"
                  step="1"
                  color={formState.errors['ready_to_build_year'] ? 'error' : 'primary'}
                  autoComplete="off"
                  registration={register('ready_to_build_year', { ...NUMERIC })}
                  placeholder={t('Year')}
                />
              </div>
            </Field>

            <Field
              id="commercial_operation_date"
              label={t('Commercial Operation Date')}
              error={formState.errors['commercial_operation_date']}
              delay={0.25}
              classes={{ field: 'space-y-1' }}
            >
              <div className="flex gap-1">
                <Select
                  id="commercial_operation_date_quarter"
                  options={quarterOptions}
                  placeholder={t('Select a quarter')}
                  color={formState.errors['commercial_operation_date_quarter'] ? 'error' : 'primary'}
                  registration={register('commercial_operation_date_quarter')}
                />
                <Input
                  id="commercial_operation_date_year"
                  type="number"
                  step="1"
                  color={formState.errors['commercial_operation_date_year'] ? 'error' : 'primary'}
                  autoComplete="off"
                  registration={register('commercial_operation_date_year', { ...NUMERIC })}
                  placeholder={t('Year')}
                />
              </div>
            </Field>
          </div>

          <Field
            id="feedstock_supply_radius"
            label={t('Feedstock supply radius')}
            error={formState.errors['feedstock_supply_radius']}
            delay={0.15}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="feedstock_supply_radius"
              type="number"
              suffix="km"
              placeholder={t('Example: 379')}
              color={formState.errors['feedstock_supply_radius'] ? 'error' : 'primary'}
              autoComplete="off"
              registration={register('feedstock_supply_radius', { ...NUMERIC })}
            />
          </Field>
          <Field
            id="feedstock_cost"
            label={t('Feedstock cost')}
            error={formState.errors['feedstock_cost']}
            delay={0.15}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="feedstock_cost"
              type="number"
              suffix="€/ton"
              placeholder={t('Example: 379')}
              color={formState.errors['feedstock_cost'] ? 'error' : 'primary'}
              autoComplete="off"
              registration={register('feedstock_cost', { ...NUMERIC })}
            />
          </Field>

          <Field
            id="upgrading_included"
            label={t('Upgrading Included')}
            error={formState.errors['upgrading_included']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <Toggle id="upgrading_included" registration={register('upgrading_included')} />
          </Field>

          <Field
            id="upgrading_technology"
            label={t('Upgrading Technology')}
            error={formState.errors['upgrading_technology']}
            delay={0}
          >
            <Select
              id="upgrading_technology"
              registration={register('upgrading_technology', { setValueAs: nullableStringSetter })}
              options={upgradingTechnologyOptions}
              placeholder={t('Select an upgrading technology option')}
              color={formState.errors['upgrading_technology'] ? 'error' : 'primary'}
            />
          </Field>
          <Field
            id="information"
            label={t('More details')}
            error={formState.errors['information']}
            delay={0.6}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea
              id="information"
              color={formState.errors['information'] ? 'error' : 'primary'}
              registration={register('information', { setValueAs: nullableStringSetter })}
              className="min-h-32"
            />
          </Field>
        </div>
      )}
    </Form>
  );
}
